/* Progressive enhancement of the Liquid directory. No location data lives here. */
(() => {
  if (customElements.get('rymar-location-finder')) return;
  const normalize = (value) => (value || '').trim().toLowerCase();
  const countries = new Map([['us', 'US'], ['usa', 'US'], ['united states', 'US'], ['united states of america', 'US'], ['ca', 'CA'], ['canada', 'CA']]);
  // Exact postal aliases only; unknown regions retain their searchable original text.
  const regions = 'AL:Alabama|AK:Alaska|AZ:Arizona|AR:Arkansas|CA:California|CO:Colorado|CT:Connecticut|DE:Delaware|DC:District of Columbia|FL:Florida|GA:Georgia|HI:Hawaii|ID:Idaho|IL:Illinois|IN:Indiana|IA:Iowa|KS:Kansas|KY:Kentucky|LA:Louisiana|ME:Maine|MD:Maryland|MA:Massachusetts|MI:Michigan|MN:Minnesota|MS:Mississippi|MO:Missouri|MT:Montana|NE:Nebraska|NV:Nevada|NH:New Hampshire|NJ:New Jersey|NM:New Mexico|NY:New York|NC:North Carolina|ND:North Dakota|OH:Ohio|OK:Oklahoma|OR:Oregon|PA:Pennsylvania|RI:Rhode Island|SC:South Carolina|SD:South Dakota|TN:Tennessee|TX:Texas|UT:Utah|VT:Vermont|VA:Virginia|WA:Washington|WV:West Virginia|WI:Wisconsin|WY:Wyoming|AB:Alberta|BC:British Columbia|MB:Manitoba|NB:New Brunswick|NL:Newfoundland and Labrador|NS:Nova Scotia|NT:Northwest Territories|NU:Nunavut|ON:Ontario|PE:Prince Edward Island|QC:Quebec|SK:Saskatchewan|YT:Yukon'.split('|').map((pair) => pair.split(':'));
  const loadLibrary = (js, css) => {
    if (window.maplibregl) return Promise.resolve(window.maplibregl);
    if (!window.rymarFinderLibrary) window.rymarFinderLibrary = new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet'; link.href = css; document.head.append(link);
      const script = document.createElement('script'); script.src = js;
      script.onload = () => resolve(window.maplibregl);
      script.onerror = () => { script.remove(); link.remove(); window.rymarFinderLibrary = null; reject(new Error('Map unavailable')); };
      document.head.append(script);
    });
    return window.rymarFinderLibrary;
  };
  customElements.define('rymar-location-finder', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const on = (node, event, handler) => node.addEventListener(event, handler, { signal: this.events.signal });
      this.on = on;
      this.search = this.querySelector('input[type="search"]');
      this.results = this.querySelector('.rymar-finder__results');
      this.popup = this.querySelector('[data-map-callout]');
      this.status = this.querySelector('.rymar-finder__map-status');
      this.country = '';
      this.entries = [...this.querySelectorAll('[data-location]')].map((row) => {
        const data = row.dataset, lat = Number(data.latitude), lon = Number(data.longitude);
        const country = countries.get(normalize(data.country)) || '';
        const region = regions.find((pair) => pair.some((value) => normalize(value) === normalize(data.province)));
        const coords = data.latitude.trim() && data.longitude.trim() && Number.isFinite(lat) && Number.isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180 ? [lon, lat] : null;
        const entry = { row, data, country, coords, select: row.querySelector('button'), place: row.querySelector('h2').textContent,
          search: normalize([data.city, data.province, data.name, data.country, country === 'US' ? 'United States USA' : country === 'CA' ? 'Canada' : '', ...(region || [])].join(' ')) };
        on(entry.select, 'click', () => {
          this.select(entry, false);
          if (this.selected === entry && matchMedia('(max-width: 1023px)').matches) {
            this.querySelector('.rymar-finder__map-area').scrollIntoView({
              behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
              block: 'start', inline: 'nearest',
            });
          }
        });
        on(row, 'click', (event) => { if (!event.target.closest('a, button')) this.select(entry, false); });
        return entry;
      });
      this.querySelector('.rymar-finder__controls').hidden = false;
      this.querySelector('.rymar-finder__map-area').hidden = false;
      this.dataset.enhanced = '';
      on(this.search, 'input', () => this.filter());
      this.querySelectorAll('button[data-country]').forEach((button) => on(button, 'click', () => {
        this.country = button.dataset.country;
        this.querySelectorAll('button[data-country]').forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
        this.filter();
      }));
      on(this.querySelector('[data-reset]'), 'click', () => {
        this.search.value = ''; this.country = '';
        this.querySelectorAll('button[data-country]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.country === '')));
        this.filter(); this.search.focus({ preventScroll: true });
      });
      on(this.querySelector('.rymar-finder__close'), 'click', () => this.close(true));
      on(this, 'keydown', (event) => { if (event.key === 'Escape' && !this.popup.hidden) { event.preventDefault(); this.close(true); } });
      this.filter();
      this.observer = new IntersectionObserver((items) => {
        if (items.some((item) => item.isIntersecting)) { this.observer.disconnect(); this.start(); }
      }, { rootMargin: '300px' });
      this.observer.observe(this);
    }
    filter() {
      const term = normalize(this.search.value);
      this.matches = this.entries.filter((entry) => {
        const visible = (!this.country || entry.country === this.country) && (!term || entry.search.includes(term));
        entry.row.hidden = !visible;
        if (entry.icon) entry.icon.hidden = !visible;
        return visible;
      });
      this.querySelector('.rymar-finder__count').textContent = `${this.matches.length} ${this.matches.length === 1 ? 'location' : 'locations'} found`;
      this.querySelector('.rymar-finder__empty').hidden = this.matches.length !== 0;
      if (this.selected && !this.matches.includes(this.selected)) this.close(false);
      clearTimeout(this.cameraTimer);
      this.cameraTimer = setTimeout(() => this.fit(), 180);
    }
    async start() {
      const signal = this.events.signal;
      if (!this.entries.some((entry) => entry.coords)) { this.status.textContent = 'Map coordinates are not available. Browse location links below.'; return; }
      try {
        const gl = await loadLibrary(this.dataset.library, this.dataset.stylesheet);
        if (signal.aborted) return;
        const container = this.querySelector('.rymar-finder__map');
        this.map = new gl.Map({ container, style: 'https://tiles.openfreemap.org/styles/positron', zoom: 1, minZoom: 1, maxZoom: 16,
          scrollZoom: false, cooperativeGestures: true, dragRotate: false, pitchWithRotate: false, touchPitch: false, attributionControl: false, renderWorldCopies: false });
        window.RymarMapStyle?.attach(this.map);
        this.map.touchZoomRotate.disableRotation();
        this.map.addControl(new gl.NavigationControl({ showCompass: false }), 'bottom-right');
        this.map.addControl(new gl.AttributionControl({ compact: false }), 'bottom-left');
        this.entries.forEach((entry) => {
          if (!entry.coords) return;
          const icon = entry.icon = document.createElement('button');
          icon.type = 'button'; icon.className = 'rymar-map-marker'; icon.setAttribute('aria-label', `Show ${entry.place}`); icon.setAttribute('aria-pressed', 'false');
          this.on(icon, 'click', () => this.select(entry, true));
          entry.marker = new gl.Marker({ element: icon }).setLngLat(entry.coords).addTo(this.map);
          icon.hidden = entry.row.hidden;
        });
        this.fit();
        this.map.once('load', () => {
          clearTimeout(this.loadTimer); this.status.hidden = true;
          this.entries.forEach((entry) => { entry.select.hidden = !entry.coords; });
        });
        this.loadTimer = setTimeout(() => { this.status.textContent = 'Map unavailable. You can still browse location links below.'; }, 15000);
        this.resize = new ResizeObserver(() => {
          this.map?.resize();
          if (!this.selected) this.fit();
        });
        this.resize.observe(container);
      } catch {
        if (!signal.aborted) this.status.textContent = 'Map unavailable. You can still browse location links below.';
      }
    }
    fit() {
      if (!this.map) return;
      const points = this.matches.filter((entry) => entry.coords).map((entry) => entry.coords);
      if (!points.length) return;
      // Instant filter framing avoids camera movement on every keystroke, including reduced motion.
      const bounds = points.reduce((b, p) => [[Math.min(b[0][0], p[0]), Math.min(b[0][1], p[1])], [Math.max(b[1][0], p[0]), Math.max(b[1][1], p[1])]], [points[0].slice(), points[0].slice()]);
      this.map.fitBounds(bounds, { padding: 48, maxZoom: points.length === 1 ? 9 : 10, duration: 0 });
    }
    select(entry, fromMarker) {
      if (!this.map || !entry.coords) return;
      clearTimeout(this.cameraTimer);
      this.close(false); this.selected = entry;
      entry.row.dataset.selected = ''; entry.select.setAttribute('aria-pressed', 'true'); entry.select.querySelector('[data-map-label]').textContent = 'Selected on map'; entry.icon.setAttribute('aria-pressed', 'true');
      this.returnFocus = fromMarker ? entry.icon : entry.select;
      this.popup.querySelector('[data-popup-name]').textContent = entry.data.name || entry.place;
      const address = this.popup.querySelector('[data-popup-address]'); address.textContent = entry.data.address; address.hidden = !entry.data.address.trim();
      const link = this.popup.querySelector('[data-popup-link]'), source = entry.row.querySelector('a[href]');
      link.hidden = !source; link.removeAttribute('href');
      if (source) { link.href = source.href; link.setAttribute('aria-label', `View ${entry.data.name || entry.place}`); }
      this.popup.hidden = false;
      this.querySelector('.rymar-finder__count').textContent = `${entry.place} selected on map. ${this.matches.length} locations found`;
      this.map.easeTo({ center: entry.coords, zoom: 9, duration: matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 200 });
      if (fromMarker && matchMedia('(min-width: 1024px)').matches) {
        const panel = this.results.getBoundingClientRect(), row = entry.row.getBoundingClientRect();
        if (row.top < panel.top) this.results.scrollTop += row.top - panel.top;
        else if (row.bottom > panel.bottom) this.results.scrollTop += row.bottom - panel.bottom;
      }
    }
    close(restore) {
      this.popup.hidden = true;
      if (this.selected) {
        delete this.selected.row.dataset.selected; this.selected.select.setAttribute('aria-pressed', 'false'); this.selected.select.querySelector('[data-map-label]').textContent = 'Show on map'; this.selected.icon?.setAttribute('aria-pressed', 'false');
        this.selected = null;
      }
      if (restore) this.returnFocus?.focus({ preventScroll: true });
    }
    disconnectedCallback() {
      this.events?.abort(); this.events = null; this.observer?.disconnect(); this.resize?.disconnect();
      clearTimeout(this.cameraTimer); clearTimeout(this.loadTimer);
      this.entries?.forEach((entry) => entry.marker?.remove()); this.map?.remove(); this.map = null;
    }
  });
})();
